import { useEffect, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_MESSAGES } from '../graphql/queries';
import { SEND_MESSAGE } from '../graphql/mutations';
import { useChatStore } from '../stores/chatStore';
import type { SendMessageInput, MessageConnection } from '../types/chat.types';

/**
 * Hook for loading and managing messages
 */
export function useMessages() {
  const {
    addMessage,
    replaceMessage,
    removeMessage,
    addMessages,
    setMessages,
    setLoadingMessages,
    setSendingMessage,
    hasMoreMessages,
    startCursor,
    userInfo,
    setChatTitle,
    setTitleForMessage,
  } = useChatStore();

  // Query for initial messages
  const { data, loading, fetchMore, refetch } = useQuery<{ messages: MessageConnection }>(GET_MESSAGES, {
    variables: {
      input: {
        last: 20, // Load last 20 messages
      },
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data?.messages) {
        const messages = data.messages.edges.map((edge) => edge.node);
        setMessages(messages);
        addMessages([], data.messages.pageInfo);
        // Set chat title and titleMessageId from server
        if (data.messages.chatTitle) {
          if (data.messages.titleMessageId) {
            setTitleForMessage(data.messages.chatTitle, data.messages.titleMessageId);
          } else {
            setChatTitle(data.messages.chatTitle);
          }
        }
      }
    },
  });

  // Update loading state
  useEffect(() => {
    setLoadingMessages(loading);
  }, [loading, setLoadingMessages]);

  // Load more messages (for infinite scroll)
  const loadMore = useCallback(async () => {
    if (!hasMoreMessages || loading) {
      return;
    }

    try {
      const result = await fetchMore({
        variables: {
          input: {
            last: 20,
            before: startCursor,
          },
        },
      });

      if (result.data?.messages) {
        const messages = result.data.messages.edges.map((edge) => edge.node);
        addMessages(messages, result.data.messages.pageInfo);
      }
    } catch (error) {
      console.error('Failed to load more messages:', error);
    }
  }, [hasMoreMessages, loading, startCursor, fetchMore, addMessages]);

  // Send message mutation
  const [sendMessageMutation] = useMutation(SEND_MESSAGE, {
    onCompleted: () => {
      setSendingMessage(false);
    },
    onError: (error) => {
      console.error('Failed to send message:', error);
      setSendingMessage(false);
    },
  });

  // Send message function
  const sendMessage = useCallback(
    async (input: SendMessageInput) => {
      setSendingMessage(true);

      // Store local images for optimistic update
      const localImages = input.images;

      // Optimistic: show user message immediately
      const optimisticId = `optimistic-${Date.now()}`;
      const optimisticMessage: import('../types/chat.types').Message = {
        id: optimisticId,
        content: input.message,
        sender: 'user',
        createdAt: new Date().toISOString(),
        messageType: localImages && localImages.length > 0 ? 'photo' : 'text',
        images: localImages || [],
      };
      addMessage(optimisticMessage);

      try {
        const result = await sendMessageMutation({
          variables: {
            input: {
              ...input,
              firstName: input.firstName || userInfo.firstName,
              lastName: input.lastName || userInfo.lastName,
              languageCode: input.languageCode || userInfo.languageCode || 'en',
              email: input.email || userInfo.email,
              pageUrl: window.location.href,
            },
          },
        });

        // Replace optimistic message with real server message
        if (result.data?.sendMessage) {
          replaceMessage(optimisticId, {
            ...result.data.sendMessage,
            images: localImages || [],
          });
        }
      } catch (error) {
        console.error('Failed to send message:', error);
        // Remove optimistic message on failure
        removeMessage(optimisticId);
        throw error;
      }
    },
    [sendMessageMutation, setSendingMessage, userInfo, addMessage, replaceMessage, removeMessage],
  );

  return {
    messages: data?.messages?.edges.map((edge) => edge.node) || [],
    loading,
    hasMore: hasMoreMessages,
    loadMore,
    sendMessage,
    refetch,
  };
}
